/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import axios from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { compact, filter, isEmpty, isNil } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { AlertBar, BrandColors, LoadingSpinner, Tabs } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { MeContext } from 'blb-contexts/Me';
import { Subject, SubjectContext } from 'blb-contexts/Subject';
import { dashboardHorizontalPadding, dashboardVerticalPadding } from 'blb-styles/constants';
import { getDocuments, getLoan } from 'blb-api/loans';
import { getUserProfile } from 'blb-api/users';
import { GetTitleOrderActivitiesResponse, getTitleOrderAndActivitiesByLoanId } from 'blb-api/title';
import { getConfig } from 'blb-utils/config';
import { errorMessage } from 'blb-utils/errors';
import { getPersona } from 'blb-persona/index';

import { LoanInfoSummary } from './LoanInfoSummary';
import { Contacts } from './Contacts';
import { DebugTab } from './DebugTab';
import { Overview } from './Overview';
import { TitleTab } from './Title';
import { FeesTabWithTimer } from './Fees';
import { AgencyConfigurationWrapper } from './AgencyConfigurationWrapper/AgencyConfigurationWrapper';
import { DocsTab } from './Docs';
import * as docUtils from 'blb-persona/common/utils/documents';
import * as Sentry from '@sentry/browser';

export const styles = StyleSheet.create({
  bannerContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: `${dashboardVerticalPadding / 2}px`,
  },
  bodyContainer: {
    backgroundColor: BrandColors.BACKGROUND_GREY,
    flexGrow: 1,
    overflow: 'auto',
    padding: `${dashboardVerticalPadding}px ${dashboardHorizontalPadding}px`,
  },
  centeredContainer: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
  dashContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  errorContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    position: 'absolute',
    top: '15%',
    width: '100%',
  },
  header: {
    borderBottom: `1px solid ${BrandColors.CLOUD_50}`,
    flexBasis: 126,
    flexGrow: 0,
    flexShrink: 0,
    padding: `32px 40px 0`,
  },
});

export namespace DashboardPage {
  export interface Props {}
}

type TabTypes = 'overview' | 'contacts' | 'title' | 'debug' | 'inbox' | 'fees' | 'docs';

export const DashboardPage: React.FC<DashboardPage.Props> = ({}) => {
  const [subject, setSubject] = useState<SubjectContext>(null);
  const [loan, setLoan] = useState<BlendBridge.API.Loan | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<TabTypes>('overview');
  const [documents, setDocuments] = useState<BlendBridge.API.Document[]>([]);
  const [docIdToRefMap, setDocIdToRefMap] = useState<
    Record<string, EClose.API.DocumentReferenceResponse> | undefined
  >();
  const [titleOrder, setTitleOrder] = useState<Title.Orders.Order | undefined>(undefined);
  const [tenant, setTenant] = useState<string | undefined>(undefined);
  const [instantTitleActivity, setInstantTitleActivity] = useState<Title.Orders.InstantTitleActivity | undefined>(
    undefined,
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const [alert, setAlert] = useState<BlendBridge.IAlert | undefined>();
  const [banner, setBanner] = useState<BlendBridge.IAlert | undefined>();
  const { loanId } = useParams<{ loanId: string }>();
  const { isFeesEnabled, isDocsTabEnabled } = getConfig();
  const me = useContext(MeContext);

  const fetchLoan = async () => {
    const { data: fetchedLoan } = await getLoan(loanId);
    setLoan(fetchedLoan);

    const subjectValues: Subject = {
      ...subject,
      loan: fetchedLoan,
    };

    if (fetchedLoan.closing) {
      subjectValues.closing = fetchedLoan.closing;
      setTenant(fetchedLoan.closing.tenant);
    }

    setSubject(subjectValues);

    return fetchedLoan;
  };

  const fetchDocs = async () => {
    try {
      const { data: fetchedDocs } = await getDocuments(loanId);
      setDocuments(fetchedDocs);
    } catch (e) {
      setDocuments([]);
      Sentry.captureException(e);
    }
  };

  /* For Hybrid or RON closings, if the user is a settlement agent and sign-ahead docs are not yet signed:
    1. Remove DOWNLOAD action from SIGN_AHEAD docs (unless document is settlement agent only)
    2. Only allow downloads if makeHybridClosingESignDocsDownloadable flag is true
    Note: Documents become downloadable again after SIGN_AHEAD docs are signed

    For eNotes (NOTE documents):
    1. Remove DOWNLOAD action if ALL of the following are true:
        a. User is a 'settlement_agent'
        b. Closing type is 'HYBRID' or 'RON'
        c. 'disableENoteDownloadBeforeSigning' FF is true
        d. eNote status is not 'COMPLETE'
    2. Show view-only mode when DOWNLOAD action is removed
  */
  const filteredDocuments = useMemo(() => {
    const isSettlementAgent = me?.principalType === 'settlement_agent';
    const closingType = loan?.closing?.closingType;
    const isHybridOrRonClosing = closingType === 'HYBRID' || closingType === 'RON';

    // If not a settlement agent, or not Hybrid/RON closing, or no docIdToRefMap, return original documents
    if (!isSettlementAgent || !isHybridOrRonClosing || !docIdToRefMap) {
      return documents;
    }

    const docsByDesignation = docUtils.filterAndGroupDocsByDesignations(documents, docIdToRefMap, [
      'NOTE',
      'SIGN_AHEAD',
    ]);

    const noteDocuments = docsByDesignation.NOTE || [];
    const signAheadDocuments = docsByDesignation.SIGN_AHEAD || [];

    const designatedIds = new Set([...noteDocuments.map(doc => doc._id), ...signAheadDocuments.map(doc => doc._id)]);
    const nonDesignatedDocs = documents.filter(document => !designatedIds.has(document._id));

    let processedNoteDocuments = [...noteDocuments];
    let processedSignAheadDocuments = [...signAheadDocuments];

    // Process NOTE documents
    // Conditions: ffDisableDownload AND eNoteStatus !== 'COMPLETE' (common conditions already checked)
    const ffDisableDownload = loan?.featureFlags?.disableENoteDownloadBeforeSigning === true;
    const eNoteStatus = loan?.closing?.eNoteData?.eNoteStatus;
    if (ffDisableDownload && eNoteStatus !== 'COMPLETE') {
      processedNoteDocuments = noteDocuments.map(doc => {
        if (Array.isArray(doc.actions) && doc.actions.includes('DOWNLOAD')) {
          return {
            ...doc,
            actions: doc.actions.filter(action => action !== 'DOWNLOAD'),
            removed: true,
          };
        }
        return doc;
      });
    }

    // Process SIGN_AHEAD documents
    // Conditions: !areSignAheadDocsSigned AND !makeHybridClosingESignDocsDownloadable (common conditions already checked)
    if (!loan?.areSignAheadDocsSigned && loan?.featureFlags?.makeHybridClosingESignDocsDownloadable === false) {
      processedSignAheadDocuments = signAheadDocuments.map(doc => {
        const hasDownloadAction = Array.isArray(doc.actions) && doc.actions.includes('DOWNLOAD');
        if (doc.type === 'CLOSING_SETTLEMENT_ONLY') {
          return doc;
        }
        if (hasDownloadAction) {
          return {
            ...doc,
            actions: doc.actions!.filter(action => action !== 'DOWNLOAD'),
            removed: true,
          };
        }
        return doc;
      });
    }

    return [...processedNoteDocuments, ...processedSignAheadDocuments, ...nonDesignatedDocs];
  }, [documents, loan, me, docIdToRefMap]);

  // Note that fetching the title order entails checking authorization for the title agent making the call
  const fetchTitleOrderAndActivities = async (): Promise<GetTitleOrderActivitiesResponse | undefined> => {
    try {
      const { data } = await getTitleOrderAndActivitiesByLoanId(loanId);
      return data;
    } catch (err) {
      return;
    }
  };

  const removeDoc = (id: string) => {
    setDocuments(filter(documents, doc => doc._id !== id));
  };

  const initialize = async () => Promise.all([fetchLoan(), fetchDocs()]);

  const initializeWithTitleOrder = async () => {
    const [fetchedLoan] = await initialize();
    if (!isEmpty(fetchedLoan)) {
      const data = await fetchTitleOrderAndActivities();
      if (isNil(data)) {
        return;
      }
      if (data.order) {
        setTitleOrder(data.order);
        setSubject({
          ...subject,
          agency: data.agency,
          loan: fetchedLoan,
        });
      }
      setTenant(data.tenant);
      const latestActivity = getInstantTitleActivity(data);
      if (latestActivity) {
        // only set instantTitleActivity if we found a relevant latest activity to display
        setInstantTitleActivity(latestActivity);
      }
    }
  };

  /**
   * If we have instantTitleActivity data, then we want to show the InstantTitleTracker if:
   * - There is no order, in which case the activity will describe something like "validation failed"
   * - There is an "automatic" aka instant title order, in which case we can show the latest activity
   * Otherwise, if we have a manual order, then showing a previous failed instant title order is probably not helpful.
   */
  const getInstantTitleActivity = (
    data: GetTitleOrderActivitiesResponse,
  ): Title.Orders.InstantTitleActivity | undefined => {
    if (!isEmpty(data.instantTitleActivities) && (!data.order || data.order.input === 'AUTOMATIC')) {
      // titleActivities is an array but the UI just shows the status of the latest one
      return data.instantTitleActivities[0];
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      try {
        setLoading(true);
        if (me?.principalType === 'title_agent') {
          await initializeWithTitleOrder();
        } else {
          await initialize();
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          let userEmail = '';
          if (me?.principalId) {
            const { data } = await getUserProfile(me.principalId);
            userEmail = data.email;
          }
          setAlert({
            message: errorMessage('FETCHING_CLOSING_NO_ACCESS', me?.principalType, false, {
              userEmail,
            }),
            theme: 'error',
          });
        } else if (axios.isAxiosError(error) && error.response?.status === 404) {
          setAlert({
            message: errorMessage('FETCHING_CLOSING_NOT_FOUND', me?.principalType, false),
            theme: 'error',
          });
        } else {
          setAlert({
            message: errorMessage('FETCHING_DASHBOARD', me?.principalType),
            theme: 'error',
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loan) {
      const persona = getPersona(me!.principalType);
      const bannerMessage = persona.getDashboardBanner(me!, loan);
      setBanner(bannerMessage);
    }
  }, [loan]);

  useEffect(() => {
    const updateDocIdToRefMap = () => {
      if (loan?.closing) {
        const auditEvents = loan.closing.auditEvents;
        const documentReferences = loan.closing.documentReferences;
        const allClosingRefs = docUtils.getAllClosingDocumentReferences(documentReferences, auditEvents);

        const docIdToRef = docUtils.convertDocumentReferencesToDocIdToRefMap(allClosingRefs);
        setDocIdToRefMap(docIdToRef);
      } else {
        setDocIdToRefMap(undefined);
      }
    };

    updateDocIdToRefMap(); // Extracted function to improve readability.
  }, [loan]);

  const getAppContent = () => {
    if (isLoading) {
      return (
        <div className={css(styles.centeredContainer)}>
          <LoadingSpinner size="lg" theme="secondary" />
        </div>
      );
    }
    if (alert) {
      return (
        <div className={css(styles.errorContainer)} id={`${alert.theme}-message`}>
          <AlertBar icon="error" message={alert.message} theme={alert.theme} />
        </div>
      );
    }

    const titleEnabled = (titleOrder || instantTitleActivity) && me?.principalType === 'title_agent';
    const isAgencyFeesEnabled = subject?.agency?.generalTitleConfiguration?.feeCollaboration;
    const tabs: TabTypes[] = compact([
      'overview',
      titleEnabled && 'title',
      loan?.contactList?.length && 'contacts',
      me?.principalType === 'ops_desk_agent' && 'debug',
      isFeesEnabled && isAgencyFeesEnabled && titleEnabled && titleOrder && 'fees',
      isDocsTabEnabled && me?.principalType === 'title_agent' && 'docs',
    ]);
    const tabsConfig = tabs.map(tabName => ({
      active: activeTab === tabName,
      disabled: false,
      id: tabName,
      label: tabName,
      onClick: () => setActiveTab(tabName),
    }));

    return (
      loan && (
        <SubjectContext.Provider value={subject}>
          <div className={css(styles.header)}>
            <LoanInfoSummary loan={loan} />
            <Tabs activeTab={activeTab} tabsConfig={tabsConfig} />
          </div>
          <div className={css(styles.bodyContainer)}>
            {banner && (
              <div className={css(styles.bannerContainer)} id="Banner">
                <AlertBar icon="error" message={banner.message} theme={banner.theme} />
              </div>
            )}
            {activeTab === 'overview' && (
              <Overview documents={filteredDocuments} initialize={initialize} loan={loan} removeDoc={removeDoc} />
            )}
            {activeTab === 'title' && me?.principalType === 'title_agent' && (
              <TitleTab instantTitleActivity={instantTitleActivity} loan={loan} order={titleOrder} />
            )}
            {activeTab === 'contacts' && <Contacts contactList={loan.contactList} />}
            {activeTab === 'debug' && <DebugTab tenant={tenant!} />}
            {activeTab === 'fees' && (
              <AgencyConfigurationWrapper section="feeCollaboration">
                <FeesTabWithTimer loan={loan} order={titleOrder!} userId={me?.principalId!} />
              </AgencyConfigurationWrapper>
            )}
            {activeTab === 'docs' && me?.principalType === 'title_agent' && (
              <DocsTab documents={documents} fetchDocs={fetchDocs} loan={loan} sessionUserId={me.principalId} />
            )}
          </div>
        </SubjectContext.Provider>
      )
    );
  };

  return <div className={css(styles.dashContainer)}>{getAppContent()}</div>;
};
